import { useState } from "react";
import Modal from "react-modal";

import { IPoliceman } from "interfaces/Policeman";
import {
  getEfficiencyReport,
  IExportEfficiencyProps,
} from "services/ReportServices";
import { fireToast } from "services/sweetAlertConfig";

import Button from "../../Button";
import Input from "../../Input";
import Select from "../../Select";

import * as S from "./styles";
import { useDataList } from "hooks/useDataList";

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
};

export function ExportEfficiencyReportModal({ isOpen, onRequestClose }: Props) {
  const [startPeriodDate, setStartPeriodDate] = useState<Date>();
  const [endPeriodDate, setEndPeriodDate] = useState<Date>();
  const [policemanId, setPolicemanId] = useState<string>("");

  const [isRequesting, setIsRequesting] = useState<boolean>(false);

  const { data: policemen } = useDataList<IPoliceman>({
    queryKey: 'listAllPolicemen',
    route: 'policemen',
    filter: {
      notPage: true,
    }
  });

  function parseDate(
    set: React.Dispatch<React.SetStateAction<Date | undefined>>,
    value: string
  ) {
    let dateNumbers: string[] | number[] = value.split("-");
    dateNumbers = dateNumbers.map((num) => {
      return Number(num);
    });
    set(new Date(dateNumbers[0], dateNumbers[1] - 1, dateNumbers[2]));
  }

  async function handleSubmit() {
    setIsRequesting(true);

    try {
      const body: IExportEfficiencyProps = {
        id: policemanId,
      };

      if (startPeriodDate && endPeriodDate) {
        body["period"] = {
          since: startPeriodDate,
          until: endPeriodDate,
        };
      }

      const response = await getEfficiencyReport(body);

      if (response.status === 201) {
        const file = new Blob([response.data], {
          type: "application/pdf",
        });

        const fileUrl = URL.createObjectURL(file);
        window.open(fileUrl);

        fireToast("O relatório foi gerado com sucesso!");
      }
    } catch {
      fireToast(
        "Não foi possível gerar o relatório: nenhum dado encontrado para esse agente"
      );
    }

    setIsRequesting(false);
  }

  function handlePolicemanSelectChange(
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    setPolicemanId(event.target.value);
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentElement={(props, children) => (
        <S.ModalContent {...props}>{children}</S.ModalContent>
      )}
      overlayElement={(props, contentElement) => (
        <S.ModalOverlay {...props}>{contentElement}</S.ModalOverlay>
      )}
    >
      <S.Wrapper>
        <S.Column>
          <S.Title>Exportar relatório de eficiência</S.Title>
          <p>
            O relatório de eficiência analisa as informações acerca da
            efetividade de um pronta-resposta com base nas suas ocorrências
            atendidas.
          </p>
          <p>
            <ul>
              <li>
                Para analisar com base em todas as ocorrências, selecione um
                agente e clique em exportar.
              </li>
              <li>
                Para analisar com base em um período, selecione um período e um
                agente e clique em exportar.
              </li>
            </ul>
          </p>
        </S.Column>
        <S.Column>
          <S.FormTitle>Período de ocorrências (opcional)</S.FormTitle>

          <S.FilterForm>
            <Input
              id="start"
              name="start"
              required={false}
              type="Date"
              label="Começando em"
              onChange={(e) => {
                parseDate(setStartPeriodDate, e.target.value);
              }}
            />
            <Input
              id="finish"
              name="finish"
              required={false}
              type="Date"
              label="E terminando em"
              onChange={(e) => {
                parseDate(setEndPeriodDate, e.target.value);
              }}
            />

            <hr />

            <Select
              id="policeman"
              name="policeman"
              onChange={handlePolicemanSelectChange}
              label="Selecionar o agente *"
              required
              placeholder="Selecione uma opção"
            >
              <option value="Padrão">Selecione um agente</option>

              {policemen?.items?.map((policeman) => (
                <option key={policeman.id} value={policeman.id}>
                  {policeman.user?.name}
                </option>
              ))}
            </Select>

            <Button
              title={
                isRequesting ? "Gerando relatório..." : "Exportar relatório"
              }
              type="button"
              onClick={handleSubmit}
              disabled={isRequesting}
            />
          </S.FilterForm>
        </S.Column>
      </S.Wrapper>
    </Modal>
  );
}
